import { Formik } from 'formik';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { ReactComponent as EyeOffIcon } from '../../../assets/img/svg/eye-off.svg';
import { ReactComponent as EyeIcon } from '../../../assets/img/svg/eye.svg';
import dlt from '../../../utils/default-lang';
import { BodyText, HeadingThree, SmallText } from '../../typography';
import Button from '../../ui/button';
import InlineLoading from '../../ui/inline-loading';
import LoginFormSchema from './login-form-rules';
import LoginFormSC, { LoginFormErrorSC, LoginFormTextInputSC } from './login-form.styled';
import ReactCodeInput from 'react-code-input';

const LoginForm = ({
  onSubmitForm,
  isLoading,
  isBadCredentials,
  handleValidateId,
  registrationStatus,
  history,
  handleStatusReset,
  sendPublicSms,
  verifyPhoneNumber,
}) => {
  const [isPasswordToggled, setIsPasswordToggled] = useState(false);
  const [isPasswordHidden, setIsPasswordHidden] = useState(false);
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [isSmsMode, setIsSmsMode] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [isSmsSending, setIsSmsSending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0); // countdown timer for resend
  const [phoneVerified, setPhoneVerified] = useState(false); // track successful verification
  const [verificationError, setVerificationError] = useState(null); // track verification error
  const wmIdRef = useRef(null);
  const timer = useRef(null);
  const resendTimerRef = useRef(null);


  const formInitialValues = {
    wm_id: '',
    password: '',
  };

  const handleNotCompletedRedirect = useCallback(() => {
    if (!wmIdRef.current) {
      return;
    }
    history.push(`/personal-information-processing/${wmIdRef.current.value}`);
  }, [history]);

  const renderActionButton = useCallback(() => {
    // V SMS móde ponecháme pôvodné tlačidlo, ale zmeníme jeho funkciu (overenie kódu)
    if (isSmsMode) {
      const disabled = smsCode.length !== 4 || isSmsSending;
      return (
        <Button
          type="PRIMARY"
          className="form-submit"
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            setIsSmsSending(true);
            setVerificationError(null);

            const onVerifySuccess = () => {
              setIsSmsSending(false);
              setPhoneVerified(true);
              setIsSmsMode(false);
              setIsPasswordHidden(false);
            };

            const onVerifyError = (error) => {
              setIsSmsSending(false);
              if (error === 'sms_code_invalid_or_expired') {
                setVerificationError('sms_code_invalid_or_expired');
              } else {
                setVerificationError('verification_error');
              }
            };

            verifyPhoneNumber({
              data: {
                login: wmIdRef.current?.value,
                sms_code: smsCode,
              },
              onSuccess: onVerifySuccess,
              onError: onVerifyError,
            });
          }}
        >
          {isSmsSending ? (
            <InlineLoading className="inline-loader" />
          ) : (
            <FormattedMessage id="login_form_confirm" defaultMessage={dlt['login_form_confirm']} />
          )}
        </Button>
      );
    }
    switch (registrationStatus?.message) {
      case 'NOT_FOUND':
        return (
          <div className="full-block centered">
            <BodyText className="bolder red">
              <FormattedMessage
                id="login_form_id_not_found"
                defaultMessage={dlt['login_form_id_not_found']}
              />
            </BodyText>
          </div>
        );
      case 'REGISTRATION_NOT_COMPLETED':
        setIsPasswordHidden(true);
        return (
          <Button
            type="PRIMARY"
            className="form-submit"
            disabled={isLoading}
            isLoading
            onClick={handleNotCompletedRedirect}
          >
            <FormattedMessage
              id="login_form_finish_registration"
              defaultMessage={dlt['login_form_finish_registration']}
            />
          </Button>
        );
      case 'REGISTERED':
        return (
          <Button type="PRIMARY" className="form-submit" disabled={isLoading}>
            <FormattedMessage id="login_form_confirm" defaultMessage={dlt['login_form_confirm']} />
          </Button>
        );
      default:
        return (
          <Button type="PRIMARY" className="form-submit" disabled={isLoading}>
            <FormattedMessage id="login_form_confirm" defaultMessage={dlt['login_form_confirm']} />
          </Button>
        );
    }
  }, [handleNotCompletedRedirect, isLoading, registrationStatus, isSmsMode, smsCode, isSmsSending, verifyPhoneNumber]);

  const onIdChange = useCallback(
    (value) => {
      if (value.length < 6) return;
      if (timer.current) clearTimeout(timer.current);
      setIsValidationLoading(true);
      timer.current = setTimeout(async () => {
        await handleValidateId(value);
        setIsValidationLoading(false);
      }, 500);
    },
    [handleValidateId]
  );

  useEffect(() => {
    if (registrationStatus?.message === 'REGISTERED') {
      const phoneVerifiedAny = registrationStatus?.phone_verified ?? registrationStatus?.phoneVerified ?? registrationStatus?.data?.phone_verified ?? registrationStatus?.data?.phoneVerified;
      if (phoneVerifiedAny === false) {
        setIsSmsMode(true);
        setIsPasswordHidden(true);
      } else {
        setIsSmsMode(false);
        setIsPasswordHidden(false);
      }
    } else {
      setIsSmsMode(false);
      setIsPasswordHidden(false);
    }
  }, [registrationStatus, sendPublicSms]);

  // Handle resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      resendTimerRef.current = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) {
              clearInterval(resendTimerRef.current);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (resendTimerRef.current) {
          clearInterval(resendTimerRef.current);
        }
      };
    }
  }, [resendCountdown]);

  // Clear SMS code when SMS mode is disabled (section is hidden)
  useEffect(() => {
    if (!isSmsMode) {
      setSmsCode('');
      setVerificationError(null);
    }
  }, [isSmsMode]);

  return (
    <LoginFormSC>
      <Formik
        enableReinitialize
        initialValues={formInitialValues}
        validationSchema={LoginFormSchema}
        onSubmit={(values) => {
          onSubmitForm(values);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
          const WmIdHasError = errors && errors.wm_id && touched.wm_id;
          const passwordHasError = errors && errors.password && touched.password;

          return (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="headline-block">
                  <HeadingThree>
                    <FormattedMessage
                      id="login_form_headline"
                      defaultMessage={dlt['login_form_headline']}
                    />
                  </HeadingThree>
                  <SmallText className="bolder black-transparent">
                    <FormattedMessage
                      id="login_form_confirm_url"
                      defaultMessage={dlt['login_form_confirm_url']}
                    />
                    <br />
                    <a href={process.env.REACT_APP_BASE_URL}>{process.env.REACT_APP_BASE_URL}</a>
                  </SmallText>
                </div>

                <div className="heading-block">
                  <BodyText className="bolder">
                    <FormattedMessage
                      id="login_form_wm_id"
                      defaultMessage={dlt['login_form_wm_id']}
                    />
                  </BodyText>
                  <div className="separator"></div>
                </div>

                <div className="full-block">
                  <FormattedMessage
                    id="login_form_wm_id_placeholder"
                    defaultMessage={dlt['login_form_wm_id_placeholder']}
                  >
                    {(placeholder) => (
                      <LoginFormTextInputSC
                        type="text"
                        name="wm_id"
                        placeholder={placeholder}
                        filled={values.wm_id}
                        hasError={WmIdHasError}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setFieldValue('wm_id', nextValue);
                          onIdChange(nextValue);
                          handleStatusReset();
                        }}
                        onBlur={handleBlur}
                        value={values.wm_id || ''}
                        ref={wmIdRef}
                      />
                    )}
                  </FormattedMessage>{' '}
                  {WmIdHasError ? (
                    <LoginFormErrorSC>
                      <FormattedMessage id={errors.wm_id} defaultMessage={dlt[errors.wm_id]} />
                    </LoginFormErrorSC>
                  ) : null}
                </div>
                {isSmsMode && (
                  <div className="sms-code-wrapper">
                    <div className="heading-block">
                      <BodyText className="bolder">
                        <FormattedMessage id="login_form_phone_code" defaultMessage="Overovací kód" />
                      </BodyText>
                      <div className="separator"></div>
                    </div>
                    <div className="full-block">
                      <ReactCodeInput
                        type="number"
                        fields={4}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={verificationError ? 'sms-code bad-sms' : 'sms-code'}
                        onChange={(val) => {
                          setSmsCode(val.replace(/\D/g, ''));
                          if (verificationError) setVerificationError(null);
                        }}
                        value={smsCode}
                      />
                      {verificationError ? (
                        <LoginFormErrorSC>
                          <FormattedMessage
                            id={verificationError}
                            defaultMessage={verificationError === 'sms_code_invalid_or_expired'
                              ? 'SMS kód je neplatný alebo vypršal'
                              : 'Chyba pri overovaní'}
                          />
                        </LoginFormErrorSC>
                      ) : null}
                      <SmallText className="black-transparent resend-info">
                        <FormattedMessage
                          id="sms_verification_not_coming"
                          defaultMessage={dlt['sms_verification_not_coming']}
                        />
                        <Button
                          type="LINK"
                          disabled={resendCountdown > 0}
                          onClick={(e) => {
                            e.preventDefault();
                            sendPublicSms({ type: 'login-phone-verification', wmNumber: wmIdRef.current?.value });
                            setSmsCode('');
                            setResendCountdown(30); // Start 30-second cooldown
                          }}
                        >
                          {resendCountdown > 0 ? (
                            `${resendCountdown}s`
                          ) : (
                            <FormattedMessage
                              id="sms_verification_resend_btn"
                              defaultMessage={dlt['sms_verification_resend_btn']}
                            />
                          )}
                        </Button>
                      </SmallText>
                    </div>
                  </div>
                )}
                {!isSmsMode && (
                  <div className={`password-wrapper ${isPasswordHidden ? 'hidden' : 'visible'}`}>
                    <div className="heading-block">
                      <BodyText className="bolder">
                        <FormattedMessage
                          id="login_form_password"
                          defaultMessage={dlt['login_form_password']}
                        />
                      </BodyText>
                      <div className="separator"></div>
                    </div>
                    <div className="full-block">
                      <div className="input-wrap">
                        <FormattedMessage
                          id="login_form_password_placeholder"
                          defaultMessage={dlt['login_form_password_placeholder']}
                        >
                          {(placeholder) => (
                            <LoginFormTextInputSC
                              type={isPasswordToggled ? 'text' : 'password'}
                              name="password"
                              placeholder={placeholder}
                              filled={values.password}
                              hasError={passwordHasError}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.password || ''}
                            />
                          )}
                        </FormattedMessage>
                        <span
                          className="eye-icon"
                          onClick={() => setIsPasswordToggled(!isPasswordToggled)}
                        >
                          {isPasswordToggled ? <EyeIcon /> : <EyeOffIcon />}
                        </span>
                      </div>

                      {passwordHasError ? (
                        <LoginFormErrorSC>
                          <FormattedMessage
                            id={errors.password}
                            defaultMessage={dlt[errors.password]}
                          />
                        </LoginFormErrorSC>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
              <div className="row">
                {isBadCredentials ? (
                  <div className="full-block centered">
                    <BodyText className="bolder red">
                      <FormattedMessage
                        id="login_form_bad_credentials"
                        defaultMessage={dlt['login_form_bad_credentials']}
                      />
                    </BodyText>
                  </div>
                ) : null}
                {phoneVerified ? (
                  <div className="full-block centered">
                    <BodyText className="bolder green">
                      <FormattedMessage
                        id="phone_verification_success"
                        defaultMessage="Telefónne číslo bolo úspešne overené"
                      />
                    </BodyText>
                  </div>
                ) : null}
                <div className="full-block">{renderActionButton()}</div>
                {isValidationLoading ? (
                  <div className="full-block">
                    <InlineLoading className="inline-loader" />
                  </div>
                ) : null}
              </div>
            </form>
          );
        }}
      </Formik>
    </LoginFormSC>
  );
};

export default withRouter(LoginForm);
